export interface BlogPost {
  id: number;
  title: string;
  date: string;
  category: string;
  author: string;
  description: string;
  imageSrc: string;
  // Detailed content for blog details page
  heroImage: string;
  content: {
    paragraphs: string[];
    images: {
      src: string;
      caption?: string;
    }[];
  };
}

export interface BlogHeadlineData {
  title: string;
  subtitle: string;
}

export interface BlogContent {
  headline: BlogHeadlineData;
  blogPosts: BlogPost[];
}

export const BLOG_CONTENT: BlogContent = {
  headline: {
    title: "Blog",
    subtitle: "Insights, tutorials, and thought leadership from our team of experts."
  },
  blogPosts: [
    {
      id: 0,
      title: "Building Resilient Webhook Handlers in AWS: Implementing DLQs for Stripe Events",
      date: "02.14.2025",
      category: "TECH",
      author: "Arnit Gashi",
      description: "Processing webhook events reliably at scale presents significant challenges for modern distributed systems. As businesses grow their payment processing operations...",
      imageSrc: "https://images.pexels.com/photos/5125366/pexels-photo-5125366.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1",
      heroImage: "https://images.pexels.com/photos/123335/pexels-photo-123335.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1",
      content: {
        paragraphs: [
          "Processing webhook events reliably at scale presents significant challenges for modern distributed systems. As businesses grow their payment processing operations, ensuring that every webhook event is processed successfully becomes critical for maintaining data consistency and providing reliable service to customers.",
          "In this article, we'll explore how to build resilient webhook handlers using AWS services, specifically focusing on implementing Dead Letter Queues (DLQs) for Stripe events. We'll cover the architecture, implementation details, and best practices for handling webhook failures gracefully.",
          "The key to building resilient webhook handlers lies in implementing proper error handling, retry mechanisms, and monitoring. By using AWS SQS with DLQs, we can ensure that failed events are captured and can be reprocessed later, preventing data loss and maintaining system reliability."
        ],
        images: [
          {
            src: "https://images.pexels.com/photos/123335/pexels-photo-123335.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
          },
          {
            src: "https://images.pexels.com/photos/5125366/pexels-photo-5125366.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1",
            caption: "AWS Architecture for Webhook Processing with DLQ Implementation"
          }
        ]
      }
    },
    {
      id: 1,
      title: "The Future of AI in Software Development: Trends and Implications",
      date: "01.22.2025",
      category: "AI",
      author: "Erion Avdiu",
      description: "Artificial intelligence is rapidly transforming how software is developed, tested, and deployed. This article explores emerging trends and their implications for developers...",
      imageSrc: "https://images.pexels.com/photos/2582937/pexels-photo-2582937.jpeg?auto=compress&cs=tinysrgb&dpr=1&w=500",
      heroImage: "https://images.pexels.com/photos/2582937/pexels-photo-2582937.jpeg?auto=compress&cs=tinysrgb&dpr=1&w=500",
      content: {
        paragraphs: [
          "Artificial intelligence is rapidly transforming how software is developed, tested, and deployed. From automated code generation to intelligent testing frameworks, AI is becoming an integral part of the software development lifecycle.",
          "This article explores the emerging trends in AI-assisted software development and their implications for developers, teams, and organizations. We'll examine how AI tools are changing the way we write, review, and maintain code.",
          "As AI continues to evolve, developers need to adapt their skills and workflows to leverage these powerful tools effectively. The future of software development will be characterized by human-AI collaboration, where developers focus on high-level design and problem-solving while AI handles routine coding tasks."
        ],
        images: [
          {
            src: "https://images.pexels.com/photos/2582937/pexels-photo-2582937.jpeg?auto=compress&cs=tinysrgb&dpr=1&w=500"
          },
          {
            src: "https://images.unsplash.com/photo-1633356122102-3fe601e05bd2?q=80&w=2940&auto=format&fit=crop",
            caption: "AI-Powered Development Tools and Their Impact on Software Engineering"
          }
        ]
      }
    },
    {
      id: 2,
      title: "Microservices vs. Monoliths: Choosing the Right Architecture",
      date: "12.05.2024",
      category: "ARCHITECTURE",
      author: "Filan Fisteku",
      description: "The debate between microservices and monolithic architectures continues to evolve. This article provides a framework for making the right choice for your specific project needs...",
      imageSrc: "https://images.pexels.com/photos/5125366/pexels-photo-5125366.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1",
      heroImage: "https://images.pexels.com/photos/5125366/pexels-photo-5125366.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1",
      content: {
        paragraphs: [
          "The debate between microservices and monolithic architectures continues to evolve as new technologies and patterns emerge. While microservices offer benefits like scalability and team autonomy, they also introduce complexity that may not be justified for all projects.",
          "This article provides a comprehensive framework for evaluating when to use microservices versus monolithic architectures. We'll examine the trade-offs, consider factors like team size, project complexity, and business requirements.",
          "The key is to choose the architecture that best fits your specific context rather than following industry trends blindly. A well-designed monolith can often outperform a poorly implemented microservices architecture."
        ],
        images: [
          {
            src: "https://images.pexels.com/photos/5125366/pexels-photo-5125366.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
          },
          {
            src: "https://images.unsplash.com/photo-1633356122102-3fe601e05bd2?q=80&w=2940&auto=format&fit=crop",
            caption: "Architecture Decision Framework: Monolith vs Microservices"
          }
        ]
      }
    },
    {
      id: 3,
      title: "Optimizing React Performance: Advanced Techniques",
      date: "11.18.2024",
      category: "FRONTEND",
      author: "Arnit Gashi",
      description: "As React applications grow in complexity, performance optimization becomes increasingly important. Learn advanced techniques to keep your React apps fast and responsive...",
      imageSrc: "https://images.unsplash.com/photo-1633356122102-3fe601e05bd2?q=80&w=2940&auto=format&fit=crop",
      heroImage: "https://images.unsplash.com/photo-1633356122102-3fe601e05bd2?q=80&w=2940&auto=format&fit=crop",
      content: {
        paragraphs: [
          "As React applications grow in complexity, performance optimization becomes increasingly important for maintaining a smooth user experience. This article covers advanced techniques for optimizing React applications, from component-level optimizations to application-wide strategies.",
          "We'll explore techniques like React.memo, useMemo, useCallback, and code splitting to improve rendering performance and reduce bundle sizes. We'll also discuss tools and methodologies for measuring and monitoring performance.",
          "Performance optimization is an ongoing process that requires careful measurement and iteration. By implementing these techniques systematically, you can significantly improve the performance of your React applications."
        ],
        images: [
          {
            src: "https://images.unsplash.com/photo-1633356122102-3fe601e05bd2?q=80&w=2940&auto=format&fit=crop"
          },
          {
            src: "https://images.pexels.com/photos/2582937/pexels-photo-2582937.jpeg?auto=compress&cs=tinysrgb&dpr=1&w=500",
            caption: "React Performance Optimization Techniques and Best Practices"
          }
        ]
      }
    }
  ]
};

// Helper function to get blog post by ID
export const getBlogPostById = (id: number): BlogPost | undefined => {
  return BLOG_CONTENT.blogPosts.find(post => post.id === id);
}; 